// Services
import breedsService from "@/app/services/main/breeds/breeds-service";
import breedingManagementService from "@/app/services/main/breeder/breeding-management-service";

// Utils
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Mixins
// import { GoogleAnalyticsMixins } from '@/app/mixins/utils/google-analytics-mixins';
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins";
import { inputTextMixins } from "@/app/mixins/utils/input-text-mixins";
import { userMixins } from "@/app/mixins/modules/user-mixins.js";
import { errorFocusMixins } from "@//app/mixins/utils/error-focus-mixins.js";
import { comingSoonMixins } from "@/app/mixins/modules/coming-soon-mixins.js";
import { SUCCESS_MSG, STATUS_CODES, WHELPING_DATE_STATUS } from '@/app/constants/constants.js';

import DatePicker from "vue2-datepicker";

// Components
const FileUpload = () =>
	import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
	);

const DOG_GENDER = {
	DAM: "Dam",
	SIRE: "Sire",
}

export default {
	name: 'BreederDamSireDetails',
	components: {
		FileUpload,
		DatePicker,
	},
	directive: {},
	filters: {},
	mixins: [
		userMixins,
		imageCompressorMixins,
		inputTextMixins,
		toastMessageMixins,
		dateMixins,
		errorFocusMixins,
		comingSoonMixins
	],
	data() {
		return {
			damSireId: null,
			breederSysId: null,
			imageTypeList: ['image/svg', 'image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/raw'],
			isAPILoading: false,
			isAPISubmitLoading: false,
			imageToCrop: "",

			breedList: [],
			genderList: [DOG_GENDER.DAM, DOG_GENDER.SIRE],

			dogImageUploadPreviewSource: "",
			dogImageToCrop: "",
			dogImageUpload: "",

			inputFields: {
				dogName: "",
				breed: "",
				gender: "",
				dateOfBirth: "",
			},

			previousWhelpingDates: [],
			previousWhelpingDatesToRemove: [],

			dogNotes: "",
			dogNotesHistory: [],

			initialValues: {},
		};
	},

	computed: {
		validationRules() {
			let fields = {
				inputFields: {
					dogName: {},
					breed: {},
					gender: {},
					dateOfBirth: {},
				},
			};

			fields.inputFields = vuelidateUtil.setFieldsRequired(fields.inputFields, true);

			return fields;
		},
		dogImageSource() {
			let img = "";

			if (this.dogImageUploadPreviewSource) {
				img = this.dogImageUploadPreviewSource;
			} else {
				img = require("@/assets/images/_shared/default-profile-pic.png");
			}

			return img;
		},
		isGenderDam() {
			return this.inputFields.gender === DOG_GENDER.DAM;
		},
	},
	async created() {
		this.breederSysId = this.$route.params.breederSysId;
		this.damSireId = this.$route.params.id;

		window.scrollTo(0, 0);

		await this.getBreedList();
		await this.getDamSireDetails();
	},
	methods: {
		async onUpdateDetails() {
			this.$v.$touch();

			if (this.$v.$invalid) {
				this.setFocusOnFirstErrorField();
				return;
			}

			this.isAPISubmitLoading = true;

			// filter out whelping dates that are saved in the DB by checking if their status is "ACTIVE"
			const emptyExistingWhelpingDates = this.previousWhelpingDates.filter(({ status, date }) => {
				return status === WHELPING_DATE_STATUS.ACTIVE && !date;
			});

			const emptyExistingWhelpingDatesIds = emptyExistingWhelpingDates.length ? emptyExistingWhelpingDates.map(({ id }) => id) : [];
			this.previousWhelpingDatesToRemove = [...this.previousWhelpingDatesToRemove, ...emptyExistingWhelpingDatesIds];

			// remove added whelping datepickers with null values
			this.previousWhelpingDates = this.previousWhelpingDates.filter(({ date }) => !!date);

			// format dates to properly save them (not index 0)
			const formattedDates = this.previousWhelpingDates.map((dateObj) => ({ ...dateObj, date: this.dateToISO(dateObj.date) }));

			// only add the previous whelping dates if the dog gender is a "Dam"
			const whelpingDates = this.inputFields.gender === DOG_GENDER.DAM ? formattedDates : []

			try {
				const dateOfBirth = this.dateToISO(this.inputFields.dateOfBirth);
				const dogImageUploadSource = this.dogImageUpload && this.dogImageUpload.length ? this.dogImageUpload[0] : null;

				const damSireDetails = {
					formData: {
						...this.inputFields,
						dateOfBirth,
						previousWhelpingDates: whelpingDates,
						previousWhelpingDatesToRemove: this.previousWhelpingDatesToRemove,
						dogNotes: this.dogNotes,
					},
					dogImageUpload: dogImageUploadSource,
				};

				const response = await breedingManagementService.updateAdminDamSire(this.breederSysId, this.damSireId, damSireDetails);

				const { SUCCESS_UPDATE_DAM_SIRE, ERROR_UPDATE_DAM_SIRE } = this.toastMessages.damSire();


				if (response.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
					this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_UPDATE_DAM_SIRE);
					await this.getDamSireDetails();

				} else {
					this.showAPIErrorToast(ERROR_UPDATE_DAM_SIRE, response);
				}

			} catch (err) {
				this.showGenericErrorToast();
			} finally {
				this.isAPISubmitLoading = false;
			}
		},
		async getDamSireDetails() {
			try {
				this.isAPILoading = true;

				const detailsResponse = await breedingManagementService.getDamSireDetails(this.breederSysId, this.damSireId);

				if (detailsResponse.status === STATUS_CODES.SUCCESS) {

					const { dogName, gender, breedId, birthdate, imageUrl, previousWhelpingDates, noteHistory } = detailsResponse.data;

					const selectedBreed = this.breedList.find(breed => breed.id === breedId);

					this.inputFields = {
						dogName,
						gender,
						dateOfBirth: new Date(birthdate),
						breed: selectedBreed
					}

					if (gender === DOG_GENDER.DAM) {
						this.previousWhelpingDates = previousWhelpingDates.map(data => {
							return { ...data, date: new Date(data.whelpingDate) };
						});
					}

					this.dogImageUploadPreviewSource = imageUrl;
					this.dogImageUpload = imageUrl;

					if (noteHistory && noteHistory.length > 0) {
						this.dogNotesHistory = noteHistory.filter(noteObj => noteObj.note !== "");
						this.dogNotes = noteHistory[0].note;
					}


					this.initialValues = {
						dogName,
						gender,
						dateOfBirth: new Date(birthdate),
						breed: selectedBreed,
						dogImageUpload: this.dogImageUpload,
						previousWhelpingDates: JSON.parse(JSON.stringify(this.previousWhelpingDates)),

						dogNotes: this.dogNotes
					}

				} else {
					const { ERROR_GET_DAM_SIRE } = this.toastMessages.damSire();
					this.showAPIErrorToast(ERROR_GET_DAM_SIRE, detailsResponse);
				}


			} catch (err) {
				this.showGenericErrorToast();
			} finally {
				this.isAPILoading = false;
			}
		},
		addWhelpingDate() {
			const newWhelpingDate = {
				id: Math.random().toString(),
				date: null,
			};

			this.previousWhelpingDates.push(newWhelpingDate);
		},
		removeWhelpingDate(whelpingDate) {
			if (!whelpingDate) {
				return;
			}

			this.previousWhelpingDates = this.previousWhelpingDates.filter(
				(dateObj) => dateObj.id !== whelpingDate.id
			);

			if (whelpingDate.id && whelpingDate.status === WHELPING_DATE_STATUS.ACTIVE) {
				this.previousWhelpingDatesToRemove.push(whelpingDate.id);
			}

		},
		sortAlphabetically(items) {
			const sortedItems = items.sort(function (a, b) {
				return a.name.localeCompare(b.name);
			});
			return sortedItems;
		},
		async getBreedList() {
			try {

				this.isAPILoading = true;

				const breedsResponse = await breedsService.getBreeds();

				if (breedsResponse.status === STATUS_CODES.SUCCESS) {
					const otherBreedOptions = breedsResponse.data.data.slice(
						0,
						4
					);
					const breeds = this.sortAlphabetically(
						breedsResponse.data.data.slice(
							4,
							breedsResponse.data.count
						)
					);

					// remove "Breed not specified", it has id = 0
					this.breedList = [...otherBreedOptions, ...breeds].filter(
						(breed) => breed.id != 0
					);
				} else {
					const { ERROR_GET_BREED_LIST } = this.toastMessages.breed();
					this.showAPIErrorToast(
						ERROR_GET_BREED_LIST,
						breedsResponse
					);
				}
			} catch (err) {
				this.showGenericErrorToast();
			} finally {
				this.isAPILoading = false;
			}
		},
		setImageCrop({ image, blob }) {
			this.dogImageUploadPreviewSource = image;
			this.dogImageToCrop = blob;
			this.dogImageUpload = blob;
		},
		async onUploadImage(event) {
			let valid = await this.validateImageWithType(event.target.files[0], this.imageTypeList);

			// do nothing when user cancels Upload
			if (!valid) {
				// reset uploader
				this.$refs.imgUploader.value = "";
				return;
			}

			// compress image if more than 2mb
			const file = await this.compressImage(event.target.files[0]);

			this.dogImageToCrop = [file];

			// show preview of pet image
			let reader = new FileReader();

			reader.onload = () => {
				this.imageToCrop = reader.result;
				this.$refs.modalFileUpload.show();
			};

			// this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.ADD_PET_IMAGE.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.ADD_PET_IMAGE.LABEL);
			reader.readAsDataURL(file);
		},

		clearFileInput(event) {
			event.target.value = "";
		},
		notAfterToday(date) {
			return date > new Date();
		},
		showComingSoonAlertModal() {
			this.showComingSoonAlert();
		},
		redirectToDocuments(){
			if(!this.damSireId){
                return;
            }
            
            this.$router.push(`/breeder-management/dam-sire/documents/${this.breederSysId}/${this.damSireId}`);
		},
		redirectToGestation() {
			this.$router.push(`/breeder-management/${this.breederSysId}/${this.damSireId}/gestation-period?from=dam-details`);
		},
	},

	validations() {
		return this.validationRules;

	},


	// third party libraries
}